import React from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { CheckCircle, DirectionsCar } from "@mui/icons-material";
import {
  PendingVehicleReview,
  VehicleTypeEntry,
  adminListVehicleTypes,
  confirmVehicleType,
  getPendingVehicleTypeReviews,
} from "../../../services/vehicleCatalog.service";
import { useAlert } from "../../../context/AlertContext";

export default function VehicleTypeReviews() {
  const { t } = useTranslation();
  const { setAlert } = useAlert();

  const [vehicles, setVehicles] = React.useState<PendingVehicleReview[]>([]);
  const [vehicleTypes, setVehicleTypes] = React.useState<VehicleTypeEntry[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<PendingVehicleReview | null>(null);
  const [confirmedType, setConfirmedType] = React.useState("");
  const [updateCatalog, setUpdateCatalog] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    Promise.all([getPendingVehicleTypeReviews(), adminListVehicleTypes()])
      .then(([v, t]) => {
        setVehicles(v);
        setVehicleTypes(t);
      })
      .catch(() => setError(t("reviews.failedToLoad")))
      .finally(() => setLoading(false));
  }, [t]);

  const openDialog = (vehicle: PendingVehicleReview) => {
    setSelected(vehicle);
    setConfirmedType(vehicle.vehicleType);
    setUpdateCatalog(true);
    setDialogOpen(true);
  };

  const handleConfirm = async () => {
    if (!selected || !confirmedType) return;
    setSaving(true);
    try {
      await confirmVehicleType(selected._id, confirmedType, updateCatalog);
      setVehicles((prev) => prev.filter((v) => v._id !== selected._id));
      setAlert("success", t("vehicleTypeReviews.confirmed"));
      setDialogOpen(false);
    } catch {
      setAlert("error", t("vehicleTypeReviews.confirmFailed"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <DirectionsCar />
        <Typography variant="h5" fontWeight={600}>
          {t("nav.vehicleTypeReviews")}
        </Typography>
        {vehicles.length > 0 && (
          <Chip label={vehicles.length} color="warning" size="small" />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {vehicles.length === 0 && !error ? (
        <Alert severity="success" icon={<CheckCircle />}>
          {t("vehicleTypeReviews.empty")}
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("vehicleTypeReviews.colBrandModel")}</TableCell>
                <TableCell>{t("vehicleTypeReviews.colPlate")}</TableCell>
                <TableCell>{t("vehicleTypeReviews.colUser")}</TableCell>
                <TableCell>{t("vehicleTypeReviews.colCurrentType")}</TableCell>
                <TableCell>{t("vehicleTypeReviews.added")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {vehicles.map((v) => (
                <TableRow key={v._id} hover>
                  <TableCell>
                    <Typography fontWeight={500}>{v.brand}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {v.model}
                    </Typography>
                  </TableCell>
                  <TableCell>{v.plateNumber}</TableCell>
                  <TableCell>
                    {v.userId ? (
                      <>
                        <Typography variant="body2">
                          {v.userId.firstName} {v.userId.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {v.userId.country_code} {v.userId.phone}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={v.vehicleType} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(v.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => openDialog(v)}
                    >
                      {t("vehicleTypeReviews.review")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("vehicleTypeReviews.confirmTitle")}</DialogTitle>
        <DialogContent>
          {selected && (
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              <Typography variant="body2" color="text.secondary">
                {selected.brand} {selected.model} · {selected.plateNumber}
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>{t("vehicleTypeReviews.vehicleType")}</InputLabel>
                <Select
                  value={confirmedType}
                  label={t("vehicleTypeReviews.vehicleType")}
                  onChange={(e: SelectChangeEvent) => setConfirmedType(e.target.value)}
                >
                  {vehicleTypes.map((t) => (
                    <MenuItem key={t.key} value={t.key}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={updateCatalog}
                    onChange={(e) => setUpdateCatalog(e.target.checked)}
                  />
                }
                label={t("vehicleTypeReviews.alsoUpdateCatalog")}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={saving || !confirmedType}
          >
            {saving ? <CircularProgress size={18} /> : t("common.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
